// filepath: apps/web/src/features/lists/tree/hooks/buildTreeIndex.ts
//
// FR-UI-VIEW-2 AC-1 / AC-2 / AC-4 — pure helper that turns the flat
// record array into the parent->children map + root-and-orphan
// index that TreeView consumes.
//
// Why a pure helper?
//   - The render recursion (TreeView -> TreeNode -> TreeNode) needs
//     O(1) parent->children lookup per render. Building a map is
//     O(n) once per dataset, then reused across the tree traversal.
//   - The orphan bucket (`roots` array) is a special case in the
//     AC-4 invariant — a record whose `parentValue` references an
//     id NOT in the record set is rendered as a root, visually
//     distinguished via a Badge tone shift in TreeNode.
//   - Keeping the helper pure (no React, no fetch, no I/O) makes
//     it directly unit-testable via the FR-UI-CORE-10 test
//     harness and Vitest's `expect(...).toEqual(...)` machinery.

import type { TreeRecord } from "../types";

export interface TreeIndex {
  /**
   * Children of each record, keyed by record id. Records without
   * children are present in the map with an empty array (so the
   * `hasChildren` check is `map.get(id).length > 0`).
   */
  childrenById: ReadonlyMap<string, TreeRecord[]>;
  /**
   * Root records — records whose `parentValue` is null OR refers
   * to a record id that is absent from the record set (the AC-4
   * orphan case).
   */
  roots: ReadonlyArray<TreeRecord>;
  /**
   * Orphan records — a subset of `roots` whose `parentValue`
   * references an absent id (NOT just `null`). Used so TreeView
   * can render a visual distinction if needed.
   */
  orphans: ReadonlyArray<TreeRecord>;
}

/**
 * Build the tree index from a flat record array.
 *
 * Algorithm:
 *   1. Walk the records once, registering each id in `childrenById`.
 *   2. Walk a second time, classifying each record:
 *      - parentValue null     -> root
 *      - parentValue known id -> child of that id
 *      - parentValue unknown id -> orphan (also root bucket)
 *   3. The orphan set is derived from the second-pass orphans.
 *
 * Complexity: O(n) time, O(n) memory. With `n` bounded by
 * TREE_RECORDS_CAP = 500, the constant is negligible.
 *
 * Pure function (no React, no I/O) so the test harness can
 * exercise it in isolation via `import { buildTreeIndex } from "..."`.
 */
export function buildTreeIndex(records: ReadonlyArray<TreeRecord>): TreeIndex {
  // Step 1 — register every id so the child-array lookup is O(1)
  // for every record, even those without children yet. Records
  // appear in the map keyed by their own id; their parent's
  // child-list grows as we walk a second pass.
  const childrenById = new Map<string, TreeRecord[]>();
  for (const r of records) {
    childrenById.set(r.id, []);
  }

  // Step 2 — classify. We collect orphans in a second array so the
  // roots/children view does not duplicate classification logic.
  const roots: TreeRecord[] = [];
  const orphans: TreeRecord[] = [];

  for (const r of records) {
    const pv = r.parentValue;
    if (pv === null) {
      // Explicit null parent -> root.
      roots.push(r);
      continue;
    }
    const parentBucket = childrenById.get(pv);
    if (!parentBucket) {
      // Parent id is absent from the record set — orphan.
      // FR-UI-VIEW-2 AC-4: render as a root, visually distinct.
      roots.push(r);
      orphans.push(r);
      continue;
    }
    parentBucket.push(r);
  }

  return {
    childrenById,
    roots,
    orphans,
  };
}

/**
 * Build the reverse map (child id -> parent id) when needed by
 * the renderer (e.g. for breadcrumbs or "move to parent" actions
 * in future FRs). Pure helper.
 */
export function buildParentMap(
  records: ReadonlyArray<TreeRecord>,
): ReadonlyMap<string, string> {
  const map = new Map<string, string>();
  for (const r of records) {
    if (r.parentValue !== null && r.parentValue.length > 0) {
      map.set(r.id, r.parentValue);
    }
  }
  return map;
}
